/**
 * AIEditBox — Sprint 2 shared primitive.
 *
 * Renders an inline "edit with AI" textbox below an EditableField. The user
 * types an instruction in natural language ("shorter", "more formal", "fix
 * the date") and posts to the Sprint 9 ai-edit endpoint:
 *
 *   POST /api/v1/agent-approvals/:id/ai-edit
 *   { fieldPath, instruction }
 *
 * Sprint 9 is not landed yet. The endpoint may return 404 OR be missing
 * entirely — the primitive degrades gracefully:
 *   - If we get a 404 (route not found) we hide the box with a disabled-state
 *     tooltip explaining the feature ships in Sprint 9.
 *   - On any other error we surface it inline so the user can retry.
 *
 * Authority: docs/STATEFUL-WORKFLOW-PLATFORM-SPEC.md §8.4 ("Each AI-editable
 * field has an <AIEditBox /> below it") + §8.6 (the AI-edit flow).
 */

import * as React from 'react';
import { Send, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import type { FieldDef } from '@argusai/shared';

interface AIEditBoxProps {
  field: FieldDef;
  approvalId: string;
  /** Called with the AI-generated new value on success. */
  onEdited: (newValue: unknown) => void;
  /** Optional className applied to the wrapper. */
  className?: string;
}

interface AiEditResponse {
  value: unknown;
}

export function AIEditBox({
  field,
  approvalId,
  onEdited,
  className,
}: AIEditBoxProps) {
  const [instruction, setInstruction] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /**
   * Sprint 9 not deployed → endpoint returns 404. We latch the 404 so the
   * primitive auto-degrades for the rest of the session.
   */
  const [endpointMissing, setEndpointMissing] = React.useState(false);

  // Don't render the box at all for non-aiEditable fields — defense in depth
  // since the auto-renderer already guards this. Cheap to keep here too.
  if (!field.aiEditable) return null;

  const disabled = submitting || endpointMissing || !instruction.trim();

  const submit = async () => {
    const text = instruction.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post<AiEditResponse>(
        `/agent-approvals/${approvalId}/ai-edit`,
        {
          fieldPath: field.path,
          instruction: text,
        },
      );
      onEdited(res.value);
      setInstruction('');
    } catch (err) {
      // api-client throws `Error` with `(error as any).status` attached.
      const e = err as { status?: number; message?: string };
      if (e?.status === 404) {
        setEndpointMissing(true);
        setError(null);
      } else {
        setError(e?.message ?? 'AI edit failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const placeholder = endpointMissing
    ? 'AI edit ships in Sprint 9 — coming soon'
    : `Tell AI how to edit this ${field.label.toLowerCase()}…`;

  return (
    <div
      className={cn(
        'flex flex-col gap-1 rounded-md border border-dashed border-primary/30 bg-primary/5 p-2',
        className,
      )}
      data-ai-edit-box="true"
      data-field-path={field.path}
    >
      <div className="flex items-center gap-1.5">
        <Sparkles
          className={cn(
            'h-3.5 w-3.5 shrink-0',
            endpointMissing ? 'text-muted-foreground' : 'text-primary',
          )}
          aria-hidden="true"
        />
        <Input
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder={placeholder}
          disabled={endpointMissing || submitting}
          aria-disabled={endpointMissing}
          className="h-7 border-0 bg-transparent text-xs shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          aria-label={`Edit ${field.label} with AI`}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => {
            void submit();
          }}
          disabled={disabled}
          aria-disabled={disabled}
          title={
            endpointMissing
              ? 'AI edit endpoint not available (Sprint 9)'
              : 'Send instruction to AI'
          }
        >
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>

      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}

      {endpointMissing && (
        <p className="text-[10px] text-muted-foreground">
          AI-assisted editing for this field ships in Sprint 9. You can still
          edit manually above.
        </p>
      )}
    </div>
  );
}
